import * as React from 'react';
import propz from 'propz';
import { Form, Formik } from 'formik';
import { REPORT_TYPE_SERVER_TO_CLIENT_MAPPING } from '../../../../../../../consts/report';
import {
  ReportActions,
  ReportDateRange,
  ReportDescription,
  ReportRadioGroup,
  ReportSection,
  ReportToggle,
  ReportToggleList
} from 'Src/views/GenericView/AdminView/TeamsAndEvents/Events/SchoolEventView/Reports/ReportFormUi';
import '../../Clubs.css';

const GROUP_BY = {
  SCHOOL_FORMS: 'isGroupByForms',
  YEAR_GROUPS: 'isGroupByYears',
  KEY_STAGES: 'isGroupByKeyStages'
};

interface Props {
  onCloseClick: () => void;
  onSubmit: (data: any, reportType: string) => void;
  activeSchoolId: string;
  school: any;
  eventReport?: boolean;
}

const getAcademicYear = (date: Date) => (date.getMonth() >= 8 ? date.getFullYear() : date.getFullYear() - 1);

// report 001
export const AggregateClubParticipationReportForm = ({
  onCloseClick,
  onSubmit,
  activeSchoolId,
  school,
  eventReport = false
}: Props) => {
  const today = new Date();
  const startOfToday = new Date(today);
  startOfToday.setHours(0, 0, 1, 0);
  const endOfToday = new Date(today);
  endOfToday.setHours(23, 59, 59, 999);
  const initialValues = {
    dateFrom: startOfToday,
    dateTo: endOfToday,
    isPrintPP: false,
    isPrintSEN: false,
    isPrintFSM: false,
    isPrintEthnicity: false,
    groupBy: GROUP_BY.SCHOOL_FORMS,
    eventSource: 'all'
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{
        ...initialValues,
        isPrintPP: false,
        isPrintSEN: false,
        isPrintFSM: false,
        isPrintEthnicity: false
      }}
      onSubmit={values => {
        const valuesToTransfer: any = { ...values };
        propz.set(valuesToTransfer, [values.groupBy], true);
        delete valuesToTransfer.groupBy;
        if (!eventReport) delete valuesToTransfer.eventSource;
        onSubmit(
          valuesToTransfer,
          eventReport
            ? REPORT_TYPE_SERVER_TO_CLIENT_MAPPING.AGGREGATE_EVENT_PARTICIPATION_FOR_PERIOD
            : REPORT_TYPE_SERVER_TO_CLIENT_MAPPING.AGGREGATE_CLUB_PARTICIPATION_FOR_PERIOD
        );
      }}
    >
      {({ values, setFieldValue }) => {
        const reportAcademicYear = getAcademicYear(values.dateFrom);
        const isCrossAcademicYear = reportAcademicYear !== getAcademicYear(values.dateTo);
        const isEnglishYearGroups = propz.get(school, ['ageGroupsNaming'], 'ENGLISH') === 'ENGLISH';
        const keyStageGroupingUnavailable = !isEnglishYearGroups;
        const hasInvalidGrouping = values.groupBy === GROUP_BY.KEY_STAGES && keyStageGroupingUnavailable;
        const startsBeforeArchive = values.dateFrom < new Date(2025, 8, 1);
        const validationMessage = isCrossAcademicYear
          ? 'Choose dates within one academic year (1 September to 31 August).'
          : hasInvalidGrouping
          ? 'Key stages are only available to schools using English year groups.'
          : startsBeforeArchive
          ? 'Before September 2025: years are estimated. The report labels sources, unknown values and limitations.'
          : 'Historical sources are used where available. Any estimates are noted alongside the results.';
        const setDateRangeValue = (field: 'dateFrom' | 'dateTo', date: Date) => setFieldValue(field, date);

        return (
          <Form className="eReportForm">
            <div className="eReportForm__scroll">
              <ReportDescription>
                Count each student once if they were listed as a participant in at least one{' '}
                {eventReport ? 'selected event' : 'club session'} during the selected period. Grouping uses the
                student's last rostered {eventReport ? 'event' : 'club session'} and available historical evidence. Monthly snapshots are dated
                observations; missing history is clearly labelled as estimated.
              </ReportDescription>

              {eventReport && (
                <ReportSection
                  number={1}
                  title="Events to include"
                  hint="Sport means every event without a club. Includes home and away fixtures; participation does not require attendance marks."
                  required
                >
                  <ReportRadioGroup
                    name="eventSource"
                    value={values.eventSource}
                    onChange={value => setFieldValue('eventSource', value)}
                    options={[
                      { value: 'clubs', label: 'Club events', hint: 'Sessions linked to a club.' },
                      { value: 'sport', label: 'Sport events', hint: 'Fixtures and other events without a club.' },
                      {
                        value: 'all',
                        label: 'Club and sport events',
                        hint: 'Both sources. Students taking part in both count once.'
                      }
                    ]}
                  />
                </ReportSection>
              )}
              <ReportSection
                number={eventReport ? 2 : 1}
                title="Reporting period"
                hint="Choose the date range covered by the report."
                required
              >
                <ReportDateRange
                  dateFrom={values.dateFrom}
                  dateTo={values.dateTo}
                  onChangeFrom={date => {
                    setDateRangeValue('dateFrom', date);
                    if (values.dateTo < date) setFieldValue('dateTo', date);
                  }}
                  onChangeTo={date => setDateRangeValue('dateTo', date)}
                />
              </ReportSection>

              <ReportSection
                number={eventReport ? 3 : 2}
                title="Group results"
                hint="Choose how participation totals are organised."
              >
                <ReportRadioGroup
                  name="groupBy"
                  value={values.groupBy}
                  onChange={value => setFieldValue('groupBy', value)}
                  options={[
                    {
                      value: GROUP_BY.SCHOOL_FORMS,
                      label: 'Registration forms',
                      hint: 'Historical form names where recorded; otherwise Unknown. Old forms are never guessed.'
                    },
                    { value: GROUP_BY.YEAR_GROUPS, label: 'Year groups', hint: 'Group students by school year.' },
                    {
                      value: GROUP_BY.KEY_STAGES,
                      label: 'Key stages',
                      hint: keyStageGroupingUnavailable
                        ? 'Only available to schools using English year groups.'
                        : 'Group students by key stage.',
                      disabled: keyStageGroupingUnavailable
                    }
                  ]}
                />
              </ReportSection>

              <ReportSection
                number={eventReport ? 4 : 3}
                title="Student breakdowns"
                hint="Historical values only. Missing values are shown as Unknown; current traits are not substituted."
              >
                <ReportToggleList>
                  <ReportToggle
                    name="isPrintPP"
                    label="Pupil premium"
                    hint="Show how many participating students are recorded as PP."
                    value={values.isPrintPP}
                    onChange={value => setFieldValue('isPrintPP', value)}
                  />
                  <ReportToggle
                    name="isPrintSEN"
                    label="Special educational needs"
                    hint="Show how many participating students are recorded as SEN."
                    value={values.isPrintSEN}
                    onChange={value => setFieldValue('isPrintSEN', value)}
                  />
                  <ReportToggle
                    name="isPrintFSM"
                    label="Free school meals"
                    hint="Show how many participating students are recorded as FSM."
                    value={values.isPrintFSM}
                    onChange={value => setFieldValue('isPrintFSM', value)}
                  />
                  <ReportToggle
                    name="isPrintEthnicity"
                    label="Ethnicity"
                    hint="Add a separate total for each recorded ethnicity code."
                    value={values.isPrintEthnicity}
                    onChange={value => setFieldValue('isPrintEthnicity', value)}
                  />
                </ReportToggleList>
              </ReportSection>
            </div>
            <ReportActions
              onCancel={onCloseClick}
              disabled={isCrossAcademicYear || hasInvalidGrouping}
              status={validationMessage}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
